import { useTranslation } from 'react-i18next';
import { StyledButton, StyledTitle } from '@selfserve/react-ui';
import { MarketingAnswersContainer } from './marketing-answers-container';
import styled from '@emotion/styled';
import { AnswerData, QuestionData } from '../data';
import { FormikProps } from 'formik';
import { FormData } from '@selfserve/react-feature-marketing-info';
import { useContext, useEffect } from 'react';
import { getKavaStepName } from '../../../../react-feature-webinar-creator/src/lib/utils/webinar-steps-helper';
import { KavaEventPrefix } from '@selfserve/react-utils';
import MarketingInfoContext from '../utils/markerting-info-context';

interface Props {
  question: QuestionData;
  isLoading: boolean;
  formik: FormikProps<FormData>;
  questionsAmount: number;
  currentPageIndex: number;
}

const StyledWelcomeTitle = styled(StyledTitle)`
  display: block;
  margin-bottom: 25px;
  text-align: center;
  line-height: normal;
  margin-bottom: 32px;
  font-family: 'Lato';
`;

const BottomMenuContainer = styled.div(({ theme }: { theme?: any }) => ({
  position: 'fixed',
  bottom: '0',
  width: '50%',
  right: '50%',
  textAlign: 'center',
  paddingLeft: '10px',
  paddingRight: '10px',
  [theme.breakpoints.down('md')]: {
    width: '100%',
    right: '0%',
  },
}));

const BackButton = styled(StyledButton, {
  shouldForwardProp: (prop) => {
    return prop !== 'hidden';
  },
})(({ hidden, theme }: { hidden: boolean; theme?: any }) => ({
  float: 'left',
  width: '75px',
  height: '32px',
  display: 'inline-block',
  fontFamily: 'Lato',
  ...(hidden && { visibility: 'hidden' }),
  '&:before': {
    content: '"..."',
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.main,
    mask: 'no-repeat url("assets/arrow-left.svg") left',
    width: '8px',
    height: '8px',
    marginRight: '5px',
  },
  '& span': {
    display: 'inline',
  },
}));

const SkipButton = styled(
  StyledButton,
  {}
)(({}: {}) => ({
  float: 'right',
  width: '70px',
  height: '32px',
  display: 'inline-block',
  fontFamily: 'Lato',
}));

const ContinueButton = styled(
  StyledButton,
  {}
)(({}: {}) => ({
  marginTop: '16px',
  fontFamily: 'Lato',
}));

const Circle = styled.div(
  ({ theme, selected }: { theme?: any; selected?: boolean }) => ({
    display: 'inline-block',
    width: '12px',
    height: '12px',
    marginRight: '4px',
    marginLeft: '4px',
    backgroundColor: selected
      ? theme.palette.primary.main
      : theme.palette.grey['400'],
    borderRadius: '50%',
    marginTop: '10px',
  })
);

export const MarketingInfoForm = ({
  question,
  isLoading,
  formik,
  questionsAmount,
  currentPageIndex,
}: Props) => {
  const { t } = useTranslation();
  const { values, setFieldValue, handleSubmit } = formik;
  const { name, possibleAnswers, type, question: questionText } = question;

  useEffect(() => {
    if (!name || values[name]) {
      return;
    }

    setFieldValue(name, [] as Array<AnswerData>);
    possibleAnswers.forEach((answer, index) => {
      setFieldValue(name + '[' + index + ']', {
        answerName: 'answer' + index,
        selected: false,
        text: answer,
      });
    });
  }, [name, possibleAnswers, setFieldValue, values]);

  const goNextOrSubmit = () => {
    handleSubmit();
  };
  const { raiseAnalyticEventsRequest } = useContext(MarketingInfoContext);

  return (
    <form onSubmit={(event) => {
      //we need this to prevent double click in case if we have focused input
      event.preventDefault();
      handleSubmit();
    }}>
      <StyledWelcomeTitle isSmall>{questionText}</StyledWelcomeTitle>
      <MarketingAnswersContainer
        type={type}
        values={values[name] || ([] as Array<AnswerData>)}
        formik={formik}
        name={name}
      />
      <ContinueButton
        type="submit"
        loading={isLoading}
        fullWidth
        datatype={`${KavaEventPrefix}Q${currentPageIndex + 1}Submit`}
        disabled={
          !(values[name] || ([] as Array<AnswerData>)).some(
            (answer) => answer.selected
          )
        }
        onMouseDown={(event) => {
          //we need this to prevent double click in case if we have focused input
          event.preventDefault();
        }}
      >
        {t('common.continue')}
      </ContinueButton>

      <BottomMenuContainer>
        <BackButton
          type="button"
          disabled={isLoading}
          hidden={currentPageIndex === 0}
          isBorderless
          fullWidth
          isLink
          onMouseDown={(event) => {
            //we need this to prevent double click in case if we have focused input
            event.preventDefault();
            raiseAnalyticEventsRequest(`${KavaEventPrefix}Q${currentPageIndex + 1}Back`);
            history.back();
          }}
        >
          {'Back'}
        </BackButton>

        {[...Array(questionsAmount)].map((v, i) => (
          <Circle key={i} selected={i === currentPageIndex} />
        ))}
        <SkipButton
          type="button"
          disabled={isLoading}
          isBorderless
          fullWidth
          isLink
          datatype={`${KavaEventPrefix}Q${currentPageIndex + 1}Skip`}
          onClick={(event) => {
            //we need this to prevent double click in case if we have focused input
            event.preventDefault();
            goNextOrSubmit();
          }}
        >
          {t('common.skip')}
        </SkipButton>
      </BottomMenuContainer>
    </form>
  );
};
